import { Card, type CardAccent } from '../Card.js';

export type FootballTokenProps = {
  /** Display label for the seat in possession, or null when no possession. */
  possessionLabel: string | null;
  /** Whether the user owns the possessing seat. */
  isYours: boolean;
  /** Current AFL phase string (e.g. "centre_bounce", "attacker_move"). */
  phase: string;
  /** Optional contest kind ("centre_bounce" | "marking_contest"). */
  contestKind?: string;
  quarter: number;
  /** Name of the cell the ball is in — rendered as the token's footer. */
  zoneName?: string | null;
};

/**
 * The football, rendered as a playable card. Sits as an overlay on
 * top of the AFL field. Same visual primitive intended for tabletop
 * print play, hence the deliberate "card" framing.
 */
export function FootballToken({
  possessionLabel,
  isYours,
  phase,
  contestKind,
  quarter,
  zoneName,
}: FootballTokenProps) {
  // Possession guernsey: yours = steel, theirs = oxblood, loose = bone.
  const accent: CardAccent = possessionLabel === null ? 'slate' : isYours ? 'indigo' : 'red';
  const headerColor =
    accent === 'indigo'
      ? { bg: 'var(--ec-pos-ruck)', ink: 'var(--ec-on-ruck)' }
      : accent === 'red'
        ? { bg: 'var(--ec-pos-forward)', ink: 'var(--ec-on-forward)' }
        : { bg: 'var(--ec-pos-event)', ink: 'var(--ec-on-event)' };
  const headerLabel = contestKind === 'centre_bounce'
    ? 'Bounce'
    : contestKind === 'marking_contest'
      ? 'Mark'
      : 'Footy';

  return (
    <Card
      // Sized by the field it sits on, not by a fixed rem. As a fixed
      // 80px card it stayed the same size while the oval letterboxed
      // down to a strip on a phone, so the ball ended up dwarfing the
      // ground it was supposed to be lying on.
      size="fluid"
      accent={accent}
      className="animate-float-bob select-none motion-reduce:animate-none"
    >
      <div
        style={{ backgroundColor: headerColor.bg, color: headerColor.ink }}
        className="flex items-center justify-between border-b border-ec-ink/30 px-1.5 py-0.5 font-display text-[9px] font-bold uppercase tracking-wider [text-shadow:0_1px_1px_rgba(19,22,27,0.3)]"
      >
        <span>{headerLabel}</span>
        <span className="tabular-nums">Q{quarter}</span>
      </div>
      <div className="flex flex-1 items-center justify-center bg-ec-art px-1 py-1.5 shadow-ec-art [background-image:linear-gradient(to_bottom,var(--ec-art-2),var(--ec-art))]">
        <FootballGlyph />
      </div>
      {/* The position label is part of the ball — the cell it's sitting in. */}
      <div
        className="border-t border-ec-ink/15 bg-ec-face-2 px-1 py-0.5 text-center font-display text-[8px] font-bold uppercase leading-tight tracking-wide text-ec-ink-soft"
        title={zoneName ?? phase}
      >
        {zoneName ?? phase}
      </div>
    </Card>
  );
}

function FootballGlyph() {
  return (
    <svg viewBox="-12 -8 24 16" className="h-full w-full" aria-label="football">
      <defs>
        <radialGradient id="ftbl-fill" cx="0.4" cy="0.35" r="0.8">
          <stop offset="0%" stopColor="#c97a3a" />
          <stop offset="100%" stopColor="#5a3210" />
        </radialGradient>
      </defs>
      <ellipse cx={0} cy={0} rx={10} ry={6} fill="url(#ftbl-fill)" stroke="#2c1808" strokeWidth={0.6} />
      {/* Centre lacing */}
      <line x1={-5} x2={5} y1={0} y2={0} stroke="#fbe9c5" strokeWidth={0.8} strokeLinecap="round" />
      <line x1={-3.2} x2={-3.2} y1={-1.2} y2={1.2} stroke="#fbe9c5" strokeWidth={0.5} />
      <line x1={-1.6} x2={-1.6} y1={-1.2} y2={1.2} stroke="#fbe9c5" strokeWidth={0.5} />
      <line x1={0} x2={0} y1={-1.2} y2={1.2} stroke="#fbe9c5" strokeWidth={0.5} />
      <line x1={1.6} x2={1.6} y1={-1.2} y2={1.2} stroke="#fbe9c5" strokeWidth={0.5} />
      <line x1={3.2} x2={3.2} y1={-1.2} y2={1.2} stroke="#fbe9c5" strokeWidth={0.5} />
      {/* End-stripes */}
      <ellipse cx={-7.5} cy={0} rx={1.2} ry={4} fill="none" stroke="#2c1808" strokeWidth={0.4} />
      <ellipse cx={7.5} cy={0} rx={1.2} ry={4} fill="none" stroke="#2c1808" strokeWidth={0.4} />
    </svg>
  );
}
